import gql from "graphql-tag"

// Profile fields /welcome reads back after each save, so the cached `me`
// stays current. Every field here is also in CURRENT_USER_QUERY
// (components/me/me.js) with the same sub-selection; `ideas` is left out on
// purpose, since writing it with fewer fields than that query asks for would
// leave the cached `me` incomplete.
// A plain field list, not a fragment: the API forwards selections to Prisma,
// which has no `Me` type, so fields inside `... on Me` would be dropped.
export const ONBOARDING_ME_FIELDS = `
    id
    firstName
    lastName
    email
    profilePic
    location
    locationCountry
    locationLat
    locationLng
    hasOwnStartup
    ownStartupUrl
    lookingToJoinStartupAsFounder
    hasIdeaForStartup
    interestedInJobsForStartups
    elevatorPitchForStartup
    whyMakeGoodCofounder
    onboardingStartedAt
    onboardingCompletedAt
    onboardingStep
    signupIntent
    onboardingPlan
    ventureWaitlistAt
    hasValidCoFounderSubscription
    subscriptionStatus
    trialEndsAt
    ideaStage {
      id
      itemId
      label
    }
    ideaIndustry {
      id
      itemId
      label
    }
    startupStage {
      id
      itemId
      label
    }
    startupIndustry {
      id
      itemId
      label
    }
    hoursPerWeekAvailable {
      id
      itemId
      label
    }
    hasSkills {
      id
      itemId
      label
    }
    wantsSkills {
      id
      itemId
      label
    }
`

export const REQUEST_SIGNUP_CODE = gql`
  mutation REQUEST_SIGNUP_CODE($email: String!, $firstName: String) {
    requestSignupCode(email: $email, firstName: $firstName) {
      ok
      reason
      retryAfterSeconds
    }
  }
`

export const VERIFY_SIGNUP_CODE = gql`
  mutation VERIFY_SIGNUP_CODE($email: String!, $code: String!) {
    verifySignupCode(email: $email, code: $code) {
      ok
      ticket
      expiresAt
      reason
      attemptsLeft
      retryAfterSeconds
    }
  }
`

export const CREATE_ACCOUNT = gql`
  mutation CREATE_ACCOUNT(
    $ticket: String!
    $firstName: String!
    $lastName: String!
    $password: String!
    $signupSource: String
    $signupIntent: String
  ) {
    createAccount(
      ticket: $ticket
      firstName: $firstName
      lastName: $lastName
      password: $password
      agreedToGdpr: true
      signupSource: $signupSource
      signupIntent: $signupIntent
    ) {
      token
      ${ONBOARDING_ME_FIELDS}
    }
  }
`

export const SAVE_ONBOARDING_STEP = gql`
  mutation SAVE_ONBOARDING_STEP($step: String!, $input: OnboardingStepInput) {
    saveOnboardingStep(step: $step, input: $input) {
      ${ONBOARDING_ME_FIELDS}
    }
  }
`

export const UPLOAD_PROFILE_PHOTO = gql`
  mutation UPLOAD_PROFILE_PHOTO($dataUrl: String!, $crop: PhotoCropInput) {
    uploadProfilePhoto(dataUrl: $dataUrl, crop: $crop) {
      id
      profilePic
    }
  }
`

export const COMPLETE_ONBOARDING = gql`
  mutation COMPLETE_ONBOARDING {
    completeOnboarding {
      ${ONBOARDING_ME_FIELDS}
    }
  }
`

export const POST_IDEA = gql`
  mutation POST_IDEA($id: ID!) {
    updateIdea(id: $id, isActive: true) {
      success
    }
  }
`

// live counts of listed founders who fit the answers so far, plus a few faces
export const ONBOARDING_MATCH_PREVIEW = gql`
  query ONBOARDING_MATCH_PREVIEW(
    $hasSkills: [Int!]
    $wantsSkills: [Int!]
    $locationLat: Float
    $locationLng: Float
    $remote: Boolean
    $hoursPerWeekAvailable: Int
  ) {
    onboardingMatchPreview(
      hasSkills: $hasSkills
      wantsSkills: $wantsSkills
      locationLat: $locationLat
      locationLng: $locationLng
      remote: $remote
      hoursPerWeekAvailable: $hoursPerWeekAvailable
    ) {
      total
      withSkills
      wantYou
      avatars
    }
  }
`

export const CHOOSE_ONBOARDING_PLAN = gql`
  mutation CHOOSE_ONBOARDING_PLAN($plan: String!) {
    chooseOnboardingPlan(plan: $plan) {
      ${ONBOARDING_ME_FIELDS}
    }
  }
`

export const START_PREMIUM_CHECKOUT = gql`
  mutation START_PREMIUM_CHECKOUT($interval: String!, $returnPath: String, $cancelPath: String) {
    startPremiumCheckout(interval: $interval, returnPath: $returnPath, cancelPath: $cancelPath) {
      url
      trialDays
    }
  }
`

export const SYNC_CHECKOUT_SESSION = gql`
  mutation SYNC_CHECKOUT_SESSION($sessionId: String!) {
    syncCheckoutSession(sessionId: $sessionId) {
      ${ONBOARDING_ME_FIELDS}
    }
  }
`

export const JOIN_VENTURE_WAITLIST = gql`
  mutation JOIN_VENTURE_WAITLIST {
    joinVentureWaitlist {
      ${ONBOARDING_ME_FIELDS}
    }
  }
`
